import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    useEffect
} from "react";

import de from "./de/translation.json";
import en from "./en/translation.json";
import fr from "./fr/translation.json";
import it from "./it/translation.json";

// Translation resources
const resources = { de, en, fr, it };

// React context für i18n
const I18nContext = createContext(null);

// Helper: "login.title" -> verschachtelte Werte holen
function getNested(obj, path) {
    if (!obj) return undefined;
    const parts = path.split(".");
    let current = obj;
    for (const p of parts) {
        if (current == null || typeof current !== "object") return undefined;
        current = current[p];
    }
    return current;
}

// Provider-Komponente
export function I18nProvider({ children }) {
    // initial language aus localStorage oder Browser ableiten
    const detectInitialLang = () => {
        if (typeof window === "undefined") return "de";

        const stored = window.localStorage.getItem("hri_lang");
        if (stored && resources[stored]) {
            return stored;
        }

        const browser = navigator.language || navigator.userLanguage || "de";
        if (browser.startsWith("de")) return "de";
        if (browser.startsWith("fr")) return "fr";
        if (browser.startsWith("it")) return "it";
        return "en";
    };

    const [lang, setLang] = useState(detectInitialLang);

    // Sprache in localStorage persistieren
    useEffect(() => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem("hri_lang", lang);
            // optional <html lang="..."> setzen
            document.documentElement.lang = lang;
        }
    }, [lang]);

    // Translation-Funktion
    const t = (key) => {
        const dict = resources[lang];
        const val = getNested(dict, key);
        return val !== undefined ? val : key;
    };

    const value = useMemo(
        () => ({
            lang,
            setLang,
            t
        }),
        [lang]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Hook zum Verwenden im Code
export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error("useI18n must be used within I18nProvider");
    }
    return ctx;
}
